import { useCallback, useMemo } from 'react';

import {
  createSpec,
  specTypes,
  useFetch,
  useDataRefId,
  useRefetch,
  useMutation,
} from '@bingads-webui-react/overreact';

import {
  fetchRequestContract,
  fetchResponseContract,
} from '../spec/fetch';

import {
  mutationRequestContract,
  mutationResponseContract,
} from '../spec/mutation';

import { environmentLookupFn } from '../../../environment/environment-instance';
import type {
  Customer,
  CustomerMutationData,
  CustomerVariables,
  FetchRefetchMutationConfig,
  MutationRequestConfig,
  ReadParameters,
  ReadRequestConfig,
} from '../../../types';

const fetchSpec = createSpec(
  fetchRequestContract, fetchResponseContract,
  specTypes.FETCH, null, environmentLookupFn
);
const refetchSpec = createSpec(
  fetchRequestContract, fetchResponseContract,
  specTypes.REFETCH, null, environmentLookupFn
);
const mutationSpec = createSpec(
  mutationRequestContract, mutationResponseContract,
  specTypes.MUTATION, null, environmentLookupFn
);


export function useCustomer(
  dataLocator: CustomerVariables,
  config: FetchRefetchMutationConfig<Customer, CustomerVariables, CustomerMutationData> = {}
) {
  const dataRefId = useDataRefId(null, environmentLookupFn);
  const defaultConfig = useMemo(() => ({

    fetch: config.fetch || {},

    refetch: config.refetch || {},

    mutation: config.mutation || {},
  }), [config]);

  const { fetch: fetchConfig, refetch, mutation } = defaultConfig;

  const fetch = useFetch(dataRefId, fetchSpec, dataLocator, fetchConfig) as [
    Customer | undefined,
    unknown,
  ];
  const refetchFn = useRefetch(dataRefId, refetchSpec, refetch);

  const read = useCallback((
    param: ReadParameters<CustomerVariables> = {},
    requestConfig?: ReadRequestConfig<Customer, CustomerVariables>
  ) => {
    refetchFn({
      variables: { ...dataLocator, ...param.variables },
    }, requestConfig);
  }, [dataLocator, refetchFn]);

  const updateFn = useMutation(dataRefId, mutationSpec, mutation.update);

  const update = useCallback((
    data: CustomerMutationData,
    ...rest: Array<MutationRequestConfig<Customer, CustomerMutationData>>
  ) => {
    updateFn({ ...dataLocator }, data, ...rest);
  }, [dataLocator, updateFn]);

  const [data, error] = fetch;

  return [{ data, error }, { read, update }];
}

export function useCustomerWithDefaultSetting({
  cid,
  uid,
}: CustomerVariables['locator']['descriptor']) {
  const customerVariables = useMemo(() => ({
    locator: {
      descriptor: { cid, uid },
    },
  }), [cid, uid]);
  return useCustomer(customerVariables);
}
